import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CustomButton,
  CustomDropdown,
  CustomFileInput,
  CustomTextInput,
} from "../../../components";
import { useSelector } from "react-redux";
import styles from "./Book.module.css";
import { FaBook, FaEye, FaList } from "react-icons/fa";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../services/Api";
import { useNavigate, useParams } from "react-router-dom";
import PDFModal from "../../../components/CustomModal/Pdfmodal";
import dayjs from "dayjs";

// ─────────────────────────────────────────────
//  Build a full PDF URL from a relative/absolute path
// ─────────────────────────────────────────────
const buildPdfUrl = (filePath) => {
  if (!filePath) return "";
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }
  const base = IMAGE_BASE_URL.endsWith("/")
    ? IMAGE_BASE_URL.slice(0, -1)
    : IMAGE_BASE_URL;
  const path = filePath.startsWith("/") ? filePath : `/${filePath}`;
  return `${base}${path}`;
};

// Shared inline styles for the editable TOC cells
const tocInputStyle = {
  width: "100%",
  border: "none",
  padding: "4px 0",
  fontSize: "var(--font-size-sm)",
  fontFamily: "var(--font-family-primary)",
  background: "transparent",
  outline: "none",
};
const tocNumInputStyle = {
  ...tocInputStyle,
  width: "70px",
  textAlign: "center",
};
const errorTextStyle = { color: "red", fontSize: "12px", marginTop: "4px" };

function BookEdit() {
  const { t, translations } = useSelector((state) => state.language || {});
  const navigate = useNavigate();
  const { id } = useParams();

  // ==================== USER / AUTH DATA ====================
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";

  // ==================== STATE ====================
  const [formData, setFormData] = useState({
    class: "",
    subject: "",
    medium: "",
    term: "",
    volume: "",
    contents_start_page: "",
    contents_end_page: "",
    original_file_name: "",
    uploaded_file_path: "",
  });
  const [tocData, setTocData] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);

  // PDF modal
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfModalUrl, setPdfModalUrl] = useState("");
  const [pdfModalTitle, setPdfModalTitle] = useState("");

  // ==================== MONTH OPTIONS (January → December) ====================
  const monthOptions = useMemo(
    () => [
      { value: "", label: t?.selectMonth || "Select Month" },
      { value: "January", label: t?.january || "January" },
      { value: "February", label: t?.february || "February" },
      { value: "March", label: t?.march || "March" },
      { value: "April", label: t?.april || "April" },
      { value: "May", label: t?.may || "May" },
      { value: "June", label: t?.june || "June" },
      { value: "July", label: t?.july || "July" },
      { value: "August", label: t?.august || "August" },
      { value: "September", label: t?.september || "September" },
      { value: "October", label: t?.october || "October" },
      { value: "November", label: t?.november || "November" },
      { value: "December", label: t?.december || "December" },
    ],
    [t],
  );

  // ==================== FETCH BOOK ====================
  const fetchBookView = useCallback(async () => {
    if (!id || !school_id) return;
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/book/view", {
        id: id,
        school_id: Number(school_id),
      });

      if (response?.data?.status && response?.data?.data) {
        const info = response.data.data.book_info?.[0] || null;
        const content = response.data.data.book_content || [];

        if (info) {
          setFormData({
            class: info.class || "",
            subject: info.subject || "",
            medium: info.medium || "",
            term: info.term || "",
            volume: info.volume || "",
            contents_start_page:
              info.contents_start_page ?? info.content_start_page ?? "",
            contents_end_page: info.contents_end_page ?? info.total_pages ?? "",
            original_file_name: info.original_file_name || "",
            uploaded_file_path: info.uploaded_file_path || "",
          });
        }

        // Keep "month" exactly as returned by the API so the dropdown pre-selects it
        const normalizedContent = content.map((item) => ({
          ...item,
          month: item.month || "",
        }));

        setTocData(normalizedContent);
      }
    } catch (error) {
      console.error("Error fetching book view:", error);
    } finally {
      setLoading(false);
    }
  }, [id, school_id]);

  useEffect(() => {
    fetchBookView();
  }, [fetchBookView]);

  // ==================== VALIDATION ====================
  // Page-overlap check between consecutive rows
  const validateRow = (data) => {
    const newErrors = {};
    data.forEach((item, index) => {
      const currentEnd = Number(item.item_end_page);
      const nextStart = Number(data[index + 1]?.item_start_page);
      if (
        data[index + 1] &&
        currentEnd &&
        nextStart &&
        currentEnd >= nextStart
      ) {
        newErrors[index] = `Cannot exceed next Start Page (${nextStart})`;
      }
    });
    return newErrors;
  };

  // Every topic row must have a month selected
  const validateMonths = () => {
    const monthErrors = {};
    (tocData || []).forEach((item, index) => {
      if (!item.month) {
        monthErrors[`month_${index}`] =
          t?.monthRequired || "Please select a month";
      }
    });
    return monthErrors;
  };

  // ==================== HANDLERS ====================
  const handleCancel = () => navigate(-1);

  const handleTocChange = (index, field, value) => {
    setTocData((prev) => {
      const updated = [...prev];
      if (field === "item_start_page" || field === "item_end_page") {
        if (value !== "" && !/^\d{0,4}$/.test(value)) return prev;
        updated[index] = {
          ...updated[index],
          [field]: value === "" ? "" : Number(value),
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }

      // Re-run page validation, keep month errors intact
      const pageErrors = validateRow(updated);
      setErrors((prevErr) => {
        const merged = { ...prevErr, ...pageErrors };
        // drop numeric (page) errors that are now resolved
        Object.keys(prevErr).forEach((k) => {
          if (/^\d+$/.test(k) && !(k in pageErrors)) delete merged[k];
        });
        // clear this row's month error once a month is chosen
        if (field === "month" && value) {
          delete merged[`month_${index}`];
        }
        return merged;
      });

      return updated;
    });
  };

  const handleSubmit = async () => {
    const allErrors = { ...validateMonths(), ...validateRow(tocData || []) };
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      return;
    }

    setGenerateLoading(true);
    try {
      const payload = {
        school_id: Number(school_id),
        book_id: id,
        content: tocData.map((item) => ({
          id: item.id,
          topic: item.topic,
          sub_topic: item.sub_topic,
          start_page: Number(item.item_start_page),
          end_page: Number(item.item_end_page),
          month: item.month || null,
          completed_date: item.completed_date
            ? dayjs(item.completed_date).format("YYYY-MM-DD")
            : null,
        })),
        updated_by: user_id,
      };

      const response = await AxiosInstance.put("/book/edit", payload);

      if (response?.data?.status) {
        navigate(-1);
      } else {
        console.error("Save failed:", response?.data?.message);
      }
    } catch (error) {
      console.error("Generate TOC error:", error);
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleViewUploadedBook = () => {
    if (formData.uploaded_file_path) {
      setPdfModalUrl(buildPdfUrl(formData.uploaded_file_path));
      setPdfModalTitle(
        formData.original_file_name ||
          t?.viewUploadedBook ||
          "View Uploaded Book",
      );
      setPdfModalOpen(true);
    }
  };

  const handlePdfModalClose = () => {
    setPdfModalOpen(false);
    setPdfModalUrl("");
    setPdfModalTitle("");
  };

  return (
    <div>
      {/* ===== BOOK INFO (read-only) ===== */}
      <div
        className={styles.header}
        style={{ justifyContent: "space-between" }}
      >
        <div className={styles.leftSection}>
          <div className={styles.headerIcon}>
            <FaBook />
          </div>
          <h1 className={styles.headerTitle}>{t?.editBook || "Edit Book"}</h1>
        </div>

        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleViewUploadedBook}
        >
          <FaEye style={{ marginRight: "8px" }} />
          {t?.viewUploadedBook || "VIEW UPLOADED BOOK"}
        </CustomButton>
      </div>

      {/* ROW 1 */}
      <div className="row">
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.class || "Class"}
            name="class_id"
            value={formData.class}
            placeholder={t?.selectClass || "Select Class"}
            disabled
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.subject || "Subject"}
            name="subject"
            value={formData.subject}
            disabled
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.subjectMedium || "Subject Medium"}
            name="medium"
            value={formData.medium}
            placeholder={t?.selectMedium || "Select Medium"}
            disabled
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.term || "Term"}
            name="term"
            value={formData.term}
            placeholder={t?.selectTerm || "Select Term"}
            disabled
          />
        </div>
      </div>

      {/* ROW 2 */}
      <div className="row">
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.volume || "Volume"}
            name="volume"
            value={formData.volume}
            placeholder={t?.selectVolume || "Select Volume"}
            disabled
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomTextInput
            label={t?.contentStartPage || "Content Start Page"}
            name="content_start_page"
            type="text"
            value={formData.contents_start_page}
            placeholder={t?.enterContentStartPage || "Enter Content Start Page"}
            maxLength={4}
            disabled
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomTextInput
            label={t?.contentEndPage || "Content End Page"}
            name="content_end_page"
            type="text"
            value={formData.contents_end_page}
            placeholder={t?.enterContentEndPage || "Enter Content End Page"}
            maxLength={4}
            disabled
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomFileInput
            label={t?.uploadBookPdf || "Upload Book (PDF)"}
            name="book_pdf"
            value={formData.original_file_name || null}
            accept=".pdf"
            placeholder={
              formData.original_file_name || t?.noFileChosen || "No file chosen"
            }
            disabled
            buttonText={t?.chooseFile || "Choose File"}
          />
        </div>
      </div>

      <div className={styles.infoNote}>
        <span>
          {t?.noteNotEditable || "Note: The above fields are not editable"}
        </span>
      </div>

      {/* ===== GENERATED TABLE OF CONTENTS ===== */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaList />
        </div>
        <h1 className={styles.headerTitle}>
          {t?.generatedTableOfContents || "Generated Table of Contents"}
        </h1>
      </div>

      <div className={styles.tocTableWrapper}>
        <table className={styles.tocTable}>
          <thead>
            <tr>
              <th>{t?.serialNo || "Serial No"}</th>
              <th>{t?.topic || "Topic"}</th>
              <th>{t?.subTopic || "Sub Topic"}</th>
              <th>
                {t?.start || "Start"} {t?.page || "Page"}
              </th>
              <th>
                {t?.end || "End"} {t?.page || "Page"}
              </th>
              <th>{t?.completionMonth || "Completion Month"}</th>
            </tr>
          </thead>
          <tbody>
            {tocData && tocData.length > 0 ? (
              tocData.map((item, index) => (
                <tr key={item.id ?? index} className={styles.tocRow}>
                  <td>{index + 1}</td>
                  <td>
                    <input
                      type="text"
                      value={item.topic || ""}
                      onChange={(e) =>
                        handleTocChange(index, "topic", e.target.value)
                      }
                      style={tocInputStyle}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.sub_topic || ""}
                      placeholder="-"
                      onChange={(e) =>
                        handleTocChange(index, "sub_topic", e.target.value)
                      }
                      style={tocInputStyle}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.item_start_page}
                      onChange={(e) =>
                        handleTocChange(
                          index,
                          "item_start_page",
                          e.target.value,
                        )
                      }
                      style={tocNumInputStyle}
                      maxLength={4}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.item_end_page}
                      onChange={(e) =>
                        handleTocChange(index, "item_end_page", e.target.value)
                      }
                      style={tocNumInputStyle}
                      maxLength={4}
                    />
                    {errors[index] && (
                      <div style={errorTextStyle}>{errors[index]}</div>
                    )}
                  </td>
                  <td>
                    <CustomDropdown
                      name={`month_${index}`}
                      value={item.month || ""}
                      onChange={(selectedOption) =>
                        handleTocChange(
                          index,
                          "month",
                          selectedOption?.value || "",
                        )
                      }
                      options={monthOptions}
                      placeholder={t?.selectMonth || "Select Month"}
                      error={errors[`month_${index}`]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className={styles.noRecords}>
                  {t?.noRecordsToDisplay || "No records to display"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ===== ACTION BUTTONS ===== */}
      <div className={styles.actions}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.backBtnSmall}
          fullWidth={false}
          onClick={handleCancel}
        >
          {t?.cancel || "CANCEL"}
        </CustomButton>

        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleSubmit}
          disabled={generateLoading}
        >
          {generateLoading ? t?.loading || "Loading..." : t?.submit || "Submit"}
        </CustomButton>
      </div>

      <PDFModal
        show={pdfModalOpen}
        onClose={handlePdfModalClose}
        pdfUrl={pdfModalUrl}
        modalTitle={pdfModalTitle}
        translations={translations}
      />
    </div>
  );
}

export default BookEdit;
